import React, { useContext, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import countryList from "react-select-country-list";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import { formatMoney, PAYMENT_RESTRICTED_COUNTRY_CODES, STORE_CONFIG } from "../config/storeConfig";
import "../css/checkout.css";

const emptyForm = { email: "", country: "", firstName: "", lastName: "", address: "", address2: "", city: "", region: "", postalCode: "" };

function createOrderNumber() {
  const unique = window.crypto && typeof window.crypto.randomUUID === "function"
    ? window.crypto.randomUUID().replace(/-/g, "")
    : `${Date.now()}${Math.random().toString(36).slice(2)}`;
  return `BV-${Date.now().toString(36).toUpperCase()}-${unique.slice(0, 12).toUpperCase()}`;
}

const CheckOut = () => {
  const { cartItems } = useContext(CartContext);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const countries = useMemo(() => countryList().getData().filter((country) => !PAYMENT_RESTRICTED_COUNTRY_CODES.includes(country.value)), []);
  const rows = cartItems.map((cart) => ({ cart, product: productData.find((item) => item.id === cart.id) })).filter((row) => row.product);
  const subtotal = rows.reduce((sum, row) => sum + Number(row.product.price) * row.cart.quantity, 0);
  const total = subtotal + STORE_CONFIG.shippingFee;
  const update = (event) => setForm((current) => ({ ...current, [event.target.name]: event.target.value }));

  const validate = () => {
    const next = {};
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) next.email = "Enter a valid email address.";
    if (!form.country) next.country = "Select a country or region.";
    if (!form.firstName.trim()) next.firstName = "Enter a first name.";
    if (!form.lastName.trim()) next.lastName = "Enter a last name.";
    if (!form.address.trim()) next.address = "Enter a delivery address.";
    if (!form.city.trim()) next.city = "Enter a city or district.";
    const region = form.region.trim().toLowerCase();
    const restrictedRegion = STORE_CONFIG.paymentRestrictedRegions.find((entry) =>
      entry.countryCode === form.country && entry.names.some((name) => region.includes(name.toLowerCase()))
    );
    if (form.country === "UA" && !region) next.region = "Enter a region so payment availability can be checked.";
    if (restrictedRegion) next.region = "The hosted payment service does not support this region.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const pay = (event) => {
    event.preventDefault();
    if (!rows.length || !validate()) return;
    const base = window.location.origin;
    const params = new URLSearchParams({
      orderNo: createOrderNumber(),
      amount: total.toFixed(2),
      currency: STORE_CONFIG.currency,
      email: form.email.trim(),
      success_url: `${base}/payment?state=success`,
      cancel_url: `${base}/payment?state=cancel`,
      title: rows[0].product.name,
    });
    window.location.assign(`/create_payment.php?${params.toString()}`);
  };

  if (!rows.length) return <main className="checkout-empty main"><h1>Your cart is empty</h1><p>Add a product before starting checkout.</p><Link to="/shop">Browse products</Link></main>;

  const field = (name, label, props = {}) => (
    <div className="form-group"><label htmlFor={name}>{label}{props.required ? " *" : ""}</label><input id={name} name={name} value={form[name]} onChange={update} aria-invalid={Boolean(errors[name])} aria-describedby={errors[name] ? `${name}-error` : undefined} {...props} />{errors[name] && <p className="error" id={`${name}-error`}>{errors[name]}</p>}</div>
  );

  return (
    <main className="main-containerCheck main"><div className="check-custom-checkout-page">
      <div className="left"><div className="check-order-info"><form onSubmit={pay} noValidate>
        <div className="checkout-heading"><p className="eyebrow">Secure checkout</p><h1>Delivery details</h1><p>Card information is entered only on the hosted payment page after this form.</p></div>
        {field("email", "Email", { type: "email", required: true, autoComplete: "email", placeholder: "you@example.com" })}
        <div className="form-group"><label htmlFor="country">Country / region *</label><select id="country" name="country" value={form.country} onChange={update} required aria-invalid={Boolean(errors.country)}><option value="">Select country / region</option>{countries.map((country) => <option key={country.value} value={country.value}>{country.label}</option>)}</select>{errors.country && <p className="error">{errors.country}</p>}</div>
        <div className="form-row">{field("firstName", "First name", { required: true, autoComplete: "given-name" })}{field("lastName", "Last name", { required: true, autoComplete: "family-name" })}</div>
        {field("address", "Address", { required: true, autoComplete: "address-line1" })}
        {field("address2", "Apartment, suite, etc. (optional)", { autoComplete: "address-line2" })}
        <div className="form-row">{field("city", "City / district", { required: true, autoComplete: "address-level2" })}{field("region", `State / province / region${form.country === "UA" ? "" : " (optional)"}`, { required: form.country === "UA", autoComplete: "address-level1" })}</div>
        {field("postalCode", "Postal code (optional where not used)", { autoComplete: "postal-code" })}
        <div className="hosted-payment-notice"><strong>Hosted card payment</strong><p>Pay Now opens a secure hosted page. Do not enter a card number anywhere in this form.</p></div>
        <button className="check-checkout-button" type="submit">PAY NOW · {formatMoney(total)}</button>
        <p className="checkout-policy-links"><Link to="/policies/payments-pricing-fraud">Payment policy</Link><span aria-hidden="true"> · </span><Link to="/policies/shipping">Shipping</Link><span aria-hidden="true"> · </span><Link to="/policies/returns">Returns</Link></p>
      </form></div></div>
      <aside className="right" aria-label="Order summary"><div className="check-order-summary"><h2>Order summary</h2><div className="check-order-details">{rows.map(({ cart, product }) => <div key={`${product.id}-${cart.quantity}`} className="check-cart-item"><img src={product.images[0]} alt="" /><div className="pro--qty"><span>{cart.quantity}</span></div><div className="check-item-details"><h3>{product.name}</h3></div><p className="cal-single">{formatMoney(Number(product.price) * cart.quantity)}</p></div>)}
        <dl className="checkout-totals"><div><dt>Subtotal</dt><dd>{formatMoney(subtotal)}</dd></div><div><dt>Shipping</dt><dd>{formatMoney(STORE_CONFIG.shippingFee)}</dd></div><div className="checkout-total"><dt>Total</dt><dd>{formatMoney(total)}</dd></div></dl>
      </div></div></aside>
    </div></main>
  );
};

export default CheckOut;
